import { useEffect, useState, useCallback, useRef } from 'react';
import { Col, Row, Select } from 'antd';
import GenericMap from '../../../components/map/GenericMap';
import fetcher from '../../../helpers/fetcher';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Product as Presentation } from '../../../types/presentation';
import { normalize } from '../../../utils/normalizeWords';
import type { Basket } from '../../../types/basket';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { downloadCSV } from '../../../helpers/utilityHelper';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { usePriceMapData } from '../../../hooks/usePriceMapDataJSX';

interface PriceLocation {
  id: string;
  productName?: string;
  price?: number;
  coordinates: {
    lat: number;
    long: number;
  };
  establishmentName?: string;
  reportedTime?: number;
  priceCount?: number;
  latestPrices?: PriceLocation[];
  latestReportedTime?: number;
  allPrices?: PriceLocation[];
}

interface PriceMapProps {
  t: (key: string) => string;
}

const PriceMap = ({ t }: PriceMapProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [priceLocations, setPriceLocations] = useState<PriceLocation[]>([]);
  const [selectedPresentations, setSelectedPresentations] = useState<number[]>([]);
  const [selectedBasket, setSelectedBasket] = useState<number | 'all' | undefined>(undefined);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('7days');
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [payload, setPayload] = useState<any>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();

  const filtersRef = useRef({
    basket: '' as string,
    presentations: [] as { label: string; value: string }[],
    timeFrame: '' as string,
  });

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchPriceLocations();
  }, [selectedPresentations, selectedBasket, selectedTimeframe]);

  const fetchFilterData = async () => {
    try {
      const basketsData = await referenceDataSyncService.getBaskets();
      setBaskets(basketsData as unknown as Basket[]);
      if (selectedBasket == null) {
        setSelectedBasket(await referenceDataSyncService.getBasicFoodBasketId());
      }
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const loadPresentationsForBasket = useCallback(async () => {
    try {
      const presentationsData = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket === 'all' ? undefined : selectedBasket
      );

      setPresentations(presentationsData);
    } catch (error) {
      console.error('Error loading presentations for basket:', error);
      setPresentations([]);
    }
  }, [selectedBasket]);

  useEffect(() => {
    loadPresentationsForBasket();
  }, [loadPresentationsForBasket]);

  const groupPricesByLocation = (locations: PriceLocation[]) => {
    const grouped = new Map<string, PriceLocation[]>();

    locations.forEach((location) => {
      const key = `${location.coordinates.lat.toFixed(4)},${location.coordinates.long.toFixed(4)}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(location);
    });

    return Array.from(grouped.entries()).map(([key, prices]) => {
      const latestPricesByProduct = new Map<string, PriceLocation>();

      prices.forEach((price) => {
        if (price.price === undefined || price.price === null) {
          return;
        }
        const productName = price.productName || 'Unknown Presentation';
        const existing = latestPricesByProduct.get(productName);

        if (!existing || price.reportedTime! > existing.reportedTime!) {
          latestPricesByProduct.set(productName, price);
        }
      });

      const latestPrices = Array.from(latestPricesByProduct.values());

      if (latestPrices.length === 0) {
        return null;
      }

      const latestReportedTime = Math.max(...latestPrices.map((p) => p.reportedTime!));

      return {
        id: `group_${key}`,
        coordinates: latestPrices[0].coordinates,
        priceCount: prices.length,
        latestPrices: latestPrices,
        latestReportedTime: latestReportedTime,
        allPrices: prices,
      };
    });
  };

  const fetchPriceLocations = async () => {
    if (selectedBasket == null) {
      return;
    }

    try {
      setLoading(true);

      const params: any = {};
      if (Array.isArray(selectedPresentations) && selectedPresentations?.length > 0) {
        params.productIds = selectedPresentations;
      }
      if (selectedBasket !== 'all') {
        params.basketId = selectedBasket;
      }
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const days = selectedTimeframe === '7days' ? 7 : selectedTimeframe === '30days' ? 30 : 90;
        const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        params.startDate = startDate.getTime().toString();
        params.endDate = now.getTime().toString();
      }

      const response = await fetcher.get(
        API_ENDPOINTS.PRICE.GET_PRICE_LOCATIONS,
        params,
        undefined,
        ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
      );

      setPayload(response.data);
      const groupedLocations = groupPricesByLocation(response.data || []).filter(
        Boolean
      ) as PriceLocation[];
      setPriceLocations(groupedLocations);
    } catch (error) {
      console.error('Error fetching price locations:', error);
      setPriceLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) {
      return 'L. 0.00';
    }
    return `L. ${price.toFixed(2)}`;
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp || isNaN(timestamp)) {
      return 'Invalid Date';
    }
    try {
      return new Date(timestamp).toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error, 'timestamp:', timestamp);
      return 'Invalid Date';
    }
  };

  const { markers, renderPopup } = usePriceMapData({
    priceLocations,
    formatPrice,
    formatDate,
    t,
  });

  const timeframeOptions = [
    { label: t('last7Days'), value: '7days' },
    { label: t('last30Days'), value: '30days' },
    { label: t('last90Days'), value: '90days' },
    { label: t('allTime'), value: 'all' },
  ];

  const presentationOptions = [
    // { label: t('allPresentations'), value: 'all' },
    ...presentations.map((presentation) => ({
      label: presentation.name,
      value: presentation.id,
    })),
  ];

  const handleBasketChange = (val: number | 'all') => {
    const basketId = val;
    setSelectedBasket(basketId);
    setSelectedPresentations([]);
  };

  const basketOptions = [
    { label: t('allBaskets'), value: 'all' },
    ...baskets.map((basket) => ({
      label: basket.name,
      value: basket.id,
    })),
  ];

  interface PayLoad {
    id: string;
    productName: string;
    price: number;
    coordinates: { lat: number; long: number };
    establishmentName: string;
    reportedTime: number;
  }

  const downloadCustomCSV = (
    fileName: string,
    payload: PayLoad[],
    appliedFilters: {
      key: string;
      label: string;
      value: any;
    }[],
    t: (key: string) => string
  ) => {
    const safeValue = (val: any): string => {
      if (val === undefined || val === null) return t('notSelected');
      if (Array.isArray(val)) return val.length ? val.join(', ') : t('notSelected');
      if (typeof val === 'string') return val.trim() || t('notSelected');
      return String(val);
    };

    const escapeCsvValue = (value: any): string => {
      const escaped = safeValue(value).replace(/"/g, '""');
      return `"${escaped}"`;
    };

    let csvContent = '';

    csvContent += `${t('filtersUsed')}\n`;
    appliedFilters.forEach((filter) => {
      csvContent += `${escapeCsvValue(filter.label)},${escapeCsvValue(filter.value)}\n`;
    });

    csvContent += `\n${t('chartData')}\n`;

    const flattenPayload = (item: PayLoad) => ({
      id: item.id,
      productName: item.productName,
      price: item.price,
      establishmentName: item.establishmentName,
      lat: item.coordinates.lat,
      long: item.coordinates.long,
      reportedTime: item.reportedTime,
    });

    const flatRows = payload.map(flattenPayload);

    const headers = Object.keys(flatRows[0]);

    csvContent += headers.map((header) => escapeCsvValue(t(`mapData.${header}`))).join(',') + '\n';

    payload.forEach((item) => {
      const row = [
        item.id,
        item.productName,
        item.price,

        item.establishmentName,
        item.coordinates.lat,
        item.coordinates.long,
        formatDate(item.reportedTime),
      ];

      csvContent += row.map(escapeCsvValue).join(',') + '\n';
    });

    downloadCSV(csvContent, fileName);
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex justify-end gap-3 text-sm md:gap-4">
          <PrimaryButton
            disabled={isDownloading || !isOnline}
            className="px-3"
            handleOnClick={() => {
              if (isDownloading) return;

              if (!filtersRef.current.basket) {
                const option = basketOptions.find((o) => o.value === selectedBasket);
                filtersRef.current.basket = option?.label as any;
              }

              const filterData = [
                {
                  key: 'basket',
                  label: t('basket'),
                  value: filtersRef.current.basket,
                },
                {
                  key: 'presentations',
                  label: t('presentations'),
                  value:
                    filtersRef.current.presentations.map((a) => a.label) || t('allPresentations'),
                },
                {
                  key: 'timeFrame',
                  label: t('timeframe'),
                  value: filtersRef.current.timeFrame || t('last7Days'),
                },
              ];

              setIsDownloading(true);
              try {
                downloadCustomCSV(t('pricesMap'), payload, filterData, t);
              } finally {
                setTimeout(() => setIsDownloading(false), 500);
              }
            }}
          >
            {t('downloadCSV')}
          </PrimaryButton>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div className="mb-2">
              <label className="text-sm font-medium text-gray-700">{t('basket')}</label>
            </div>
            <Select
              className="w-full [&>.ant-select-selector]:!rounded-lg"
              placeholder={t('basketPlaceholder')}
              value={selectedBasket}
              onChange={(value, option) => {
                filtersRef.current.basket = (option as any).label;
                handleBasketChange(value);
              }}
              options={basketOptions}
              showSearch
              filterOption={(input, option) =>
                normalize(option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(normalize(input.toLowerCase()))
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div className="mb-2">
              <label className="text-sm font-medium text-gray-700">{t('presentations')}</label>
            </div>
            <Select
              className="w-full [&>.ant-select-selector]:!rounded-lg"
              placeholder={t('presentationsPlaceholder')}
              value={selectedPresentations}
              onChange={(vals, options) => {
                filtersRef.current.presentations = options as any;
                setSelectedPresentations(vals);
              }}
              options={presentationOptions.sort((a, b) => a.label.localeCompare(b.label))}
              allowClear
              onClear={() => setSelectedPresentations([])}
              showSearch
              mode="multiple"
              maxTagCount={1}
              maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div className="mb-2">
              <label className="text-sm font-medium text-gray-700">{t('timeframe')}</label>
            </div>
            <Select
              className="w-full [&>.ant-select-selector]:!rounded-lg"
              placeholder={t('timeframePlaceholder')}
              value={selectedTimeframe}
              onChange={(selection, option) => {
                filtersRef.current.timeFrame = (option as any).label;
                setSelectedTimeframe(selection);
              }}
              options={timeframeOptions}
            />
          </Col>
        </Row>
      </div>

      <div className="relative h-[600px] w-full rounded-lg border border-gray-300">
        <GenericMap
          markers={markers}
          center={[15.2, -100.2]}
          zoom={2}
          height="100%"
          loading={loading}
          renderPopup={renderPopup}
          onMarkerClick={(marker) => {}}
        />
      </div>

      <div className="mb-4 mt-4 text-sm text-gray-600">
        {t('showing')} {priceLocations.length} {t('priceReports')}
      </div>
    </div>
  );
};

export default PriceMap;
